import React, { useEffect, useState } from "react";
import { CalendarDays, Loader2, Sparkles, RefreshCw, Clock, Copy, Check, CalendarClock } from "lucide-react";
import { getContentCalendar, buildContentCalendar, scheduleWholeWeek } from "../../api";
import { useToast } from "../../hooks/use-toast";

const input = "w-full h-[46px] px-4 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]";

const PILLAR_COLOURS = {
  Properties: "bg-[#b8935f]/12 text-[#8a6a38] border-[#e2d0b4]",
  Local: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Educational: "bg-blue-50 text-blue-700 border-blue-200",
  Authority: "bg-purple-50 text-purple-700 border-purple-200",
  Brand: "bg-rose-50 text-rose-700 border-rose-200",
  Engagement: "bg-amber-50 text-amber-700 border-amber-200",
};

const fmtDate = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); } catch { return ""; } };

const DayRow = ({ d }) => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(d.caption || d.idea || ""); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch { toast({ title: "Couldn't copy" }); } };
  return (
    <div className="bg-white rounded-xl border border-[#f0ece5] p-4 flex flex-col sm:flex-row sm:items-start gap-3" data-testid="calendar-day">
      <div className="sm:w-28 shrink-0">
        <p className="text-[14px] font-500 text-[#2b2b2b]">{d.day}</p>
        <p className="text-[11px] text-[#a0a0a0]">{fmtDate(d.date)}</p>
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 flex-wrap mb-1.5">
          <span className={`text-[11px] px-2 py-0.5 rounded-full border ${PILLAR_COLOURS[d.pillar] || "bg-gray-50 text-gray-600 border-gray-200"}`}>{d.pillar}</span>
          <span className="text-[11px] text-[#8a8a8a]">{d.format} · {d.platform}</span>
          {d.bestTime && <span className="inline-flex items-center gap-1 text-[11px] text-[#a0a0a0]"><Clock size={11} /> {d.bestTime}</span>}
        </div>
        <p className="text-[13px] text-[#3a3a3a] font-500">{d.idea}</p>
        {d.caption && <p className="text-[12px] text-[#8a8a8a] mt-1 leading-relaxed">{d.caption}</p>}
      </div>
      <button onClick={copy} className="inline-flex items-center gap-1 text-[12px] text-[#8a8a8a] hover:text-[#b8935f] transition-colors shrink-0" data-testid="calendar-copy">{copied ? <Check size={13} /> : <Copy size={13} />}</button>
    </div>
  );
};

const ContentCalendar = () => {
  const { toast } = useToast();
  const [cal, setCal] = useState(null);
  const [focus, setFocus] = useState("");
  const [busy, setBusy] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { getContentCalendar().then(setCal).catch(() => setCal({ days: [] })).finally(() => setLoading(false)); }, []);

  const build = async () => {
    setBusy(true);
    try {
      const r = await buildContentCalendar({ focus: focus.trim() });
      setCal(r);
      toast({ title: "Weekly calendar built", description: "Balanced across all 6 content pillars." });
    } catch (e) {
      toast({ title: "Couldn't build calendar", description: e?.response?.data?.detail || "Please try again." });
    } finally { setBusy(false); }
  };

  const scheduleWeek = async () => {
    setScheduling(true);
    try {
      const r = await scheduleWholeWeek();
      toast({ title: `Whole week scheduled (${r.scheduled} posts)`, description: "Each post is queued at its best time. Enable Auto-Publish in Grow → Automation to go live automatically." });
    } catch (e) {
      toast({ title: "Couldn't schedule week", description: e?.response?.data?.detail || "Please try again." });
    } finally { setScheduling(false); }
  };

  const days = cal?.days || [];

  return (
    <div data-testid="content-calendar">
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6 mb-6">
        <h3 className="font-display text-[22px] font-400 text-[#2b2b2b] mb-1 flex items-center gap-2"><CalendarDays size={20} className="text-[#b8935f]" /> Weekly Content Calendar</h3>
        <p className="text-[13px] text-[#8a8a8a] mb-4">Auto-build a balanced 7-day plan across your six content pillars — Properties, Local, Educational, Authority, Brand & Engagement.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="Optional emphasis this week (e.g. lettings push, new development launch)" className={input + " flex-1"} data-testid="calendar-focus" />
          <button onClick={build} disabled={busy} className="inline-flex items-center justify-center gap-2 px-6 h-[46px] rounded-lg bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60 shrink-0" data-testid="calendar-build">
            {busy ? <><Loader2 size={16} className="animate-spin" /> Building week…</> : days.length ? <><RefreshCw size={16} /> Regenerate week</> : <><Sparkles size={16} /> Build my week</>}
          </button>
        </div>
        {cal?.generatedAt && <p className="text-[12px] text-[#a0a0a0] mt-3">Last generated {new Date(cal.generatedAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} by {cal.generatedBy || "you"}.</p>}
        {days.length > 0 && (
          <button onClick={scheduleWeek} disabled={scheduling} className="mt-4 w-full inline-flex items-center justify-center gap-2 h-[48px] rounded-lg bg-[#1c1c1e] text-white text-[14px] font-500 hover:bg-black transition-colors disabled:opacity-60" data-testid="calendar-schedule-week">
            {scheduling ? <><Loader2 size={16} className="animate-spin" /> Scheduling every post…</> : <><CalendarClock size={16} /> Schedule the whole week</>}
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-[#8a8a8a]"><Loader2 className="animate-spin mr-2" size={18} /> Loading…</div>
      ) : days.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#e2d8c8] bg-white/60 p-10 text-center">
          <CalendarDays size={28} className="text-[#d9c4a6] mx-auto mb-3" />
          <p className="text-[14px] text-[#8a8a8a]">No calendar yet — click <strong>Build my week</strong> to generate a balanced 7-day plan.</p>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="calendar-days">{days.map((d, i) => <DayRow key={i} d={d} />)}</div>
      )}
    </div>
  );
};

export default ContentCalendar;
